import type {Address, City, County} from "~/types/Address";
import exp from "node:constants";
import {createDefaultAddress} from "~/types/Address";

export interface Driver {
    last_name: string | null;
    first_name: string | null;
    cnp: string | null;
    series_ci: string | null;
    nr_ci: string | null;
}

export interface RcaCustomerForm extends CustomerForm {
    is_same_driver: boolean | null;
    number_of_other_drivers: number | null;
    other_drivers: Driver[] | null;
    customer_agreement: boolean,
}

export const defaultRcaCustomerForm = (): RcaCustomerForm => {
    return {
        is_legal: false,
        last_name: null,
        first_name: null,
        cnp: null,
        company_name: null,
        cif_code: null,
        series_ci: null,
        nr_ci: null,
        email: null,
        is_same_driver: true,
        number_of_other_drivers: 0,
        other_drivers: [],
        caen_code: null,
        phone_number: null,
        customer_agreement: false,
    }
}

export interface CustomerForm {
    is_legal: boolean | null;
    last_name: string | null;
    first_name: string | null;
    cnp: string | null;
    company_name: string | null;
    caen_code: string | number | null;
    cif_code: string | null;
    series_ci: string | null;
    nr_ci: string | null;
    email: string | null;
    phone_number: string | null;
}

export interface HouseCustomerForm extends CustomerForm {
    is_owner: boolean;
    is_politically_exposed: boolean;
    address: Address;
}

export const createDefaultHouseCustomerForm = (): HouseCustomerForm => {
    return {
        is_owner: true,
        is_legal: null,
        last_name: null,
        first_name: null,
        cnp: null,
        company_name: null,
        caen_code: null,
        cif_code: null,
        series_ci: null,
        nr_ci: null,
        email: null,
        phone_number: null,
        is_politically_exposed: false,
        address: createDefaultAddress(),
    }
}

export interface CustomerFormValidation {
    last_name: boolean
    first_name: boolean
    cnp: boolean
    company_name: boolean;
    caen_code: boolean;
    cif_code: boolean;
    series_ci: boolean;
    nr_ci: boolean;
    email: boolean;
    phone_number: boolean;
    [key: string]: boolean; // Add index signature
}

export interface HouseCustomerFormValidation extends CustomerFormValidation {
    is_owner: boolean;
    is_politically_exposed: boolean;
}

export interface HouseCustomersFormValidation {
    [key: string]: HouseCustomerFormValidation | null;
}

export const createDefaultHouseCustomerFormValidation = (): HouseCustomerFormValidation => {
    return {
        is_owner: true,
        last_name: false,
        first_name: false,
        cnp: false,
        company_name: false,
        caen_code: false,
        cif_code: false,
        series_ci: false,
        nr_ci: false,
        email: false,
        phone_number: false,
        is_politically_exposed: true,
    }
}

export const createDefaultCustomerForm: CustomerForm = {
    is_legal: null,
    last_name: null,
    first_name: null,
    cnp: null,
    company_name: null,
    caen_code: null,
    cif_code: null,
    series_ci: null,
    nr_ci: null,
    email: null,
    phone_number: null,
}


export interface RcaCustomerFormValidation extends CustomerFormValidation {
    customer_agreement: boolean;
    [key: string]: boolean; // Add index signature
}

export interface RcaDriverFormValidation {
    last_name: boolean
    first_name: boolean
    cnp: boolean
    series_ci: boolean
    nr_ci: boolean
    [key: string]: boolean;
}

export interface RcaDriversFormValidation {
    [key: string]: RcaDriverFormValidation | null;
}

export const createDefaultRcaCustomerFormValidation = (): RcaCustomerFormValidation => {
    return {
        last_name: false,
        first_name: false,
        cnp: false,
        company_name: false,
        cif_code: false,
        series_ci: false,
        nr_ci: false,
        email: false,
        phone_number: false,
        caen_code: false,
        customer_agreement: false,
    }
}

export interface ApiCompanyData {
    cif_code: number | null;
    company_name: string | null;
    phone_number: string | null;
    postal_code: string | null;
    caen_code: string | null;
    iban: string | null;
    county: County | null;
    city: City | null;
    street_name: string | null;
    street_number: string | null;
}

export interface ApiCompanyCaen {
    code: string | null;
    name: string | null;
    division: string | null;
    section: string | null;
    sub_section: string | null;
    group: string | null;
    nace: string | null;
    class: string | null;
}

export const createDefaultApiCompanyData = (): ApiCompanyData => {
    return {
        cif_code: null,
        company_name: null,
        phone_number: null,
        postal_code: null,
        caen_code: null,
        iban: null,
        county: null,
        city: null,
        street_name: null,
        street_number: null,
    }
}

export interface UserDataForm {
    id: number | null;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    token: string | null;
}

export const createDefaultUserData = (): UserDataForm => {
    return {
        id: null,
        email: null,
        first_name: null,
        last_name: null,
        phone_number: null,
        token: null,
    }
}

export interface UserDataValidation {
    email: boolean;
    first_name: boolean;
    last_name: boolean;
    phone_number: boolean;
    [key: string]: boolean;
}

export const createDefaultUserDataValidation = (): UserDataValidation => {
    return {
        email: false,
        first_name: false,
        last_name: false,
        phone_number: false,
    }
}